import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import Tooltip from '@components/tooltip';

import styles from './styles.module.scss';
const {
  container,
  track,
  center_label,
  label_with_tooltip,
  info_icon,
  center_value,
  status_indicator,
  instruction_text,
  legend_wrapper,
  condensed,
  bar_container,
  bar_track,
  bar_fill,
  bar_handle,
  bar_values,
  bar_percentage,
  bar_status
} = styles;

const DebtToIncomeSlider = ({
  value = 0.28,
  onChange,
  readOnly = true,
  condensed: condensedProp = false
}) => {
  // Convert decimal to percentage (0.28 -> 28)
  const [localValue, setLocalValue] = useState(Math.round(value * 100));
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef(null);
  const barRef = useRef(null);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(Math.round(value * 100));
  }, [value]);

  const percentValue = localValue;

  const getStatus = (val) => {
    if (val <= 28) return { text: 'LOW', color: '#22c55e' };
    if (val <= 36) return { text: 'MEDIUM', color: '#eab308' };
    return { text: 'HIGH', color: '#ef4444' };
  };

  const status = getStatus(percentValue);

  // Calculate position along arc for the handle
  // Scale down dimensions in condensed mode for narrow containers
  const svgWidth = condensedProp ? 260 : 320;
  const radius = condensedProp ? 115 : 140;
  const strokeWidth = condensedProp ? 16 : 20;
  const centerX = svgWidth / 2;
  const centerY = 160;
  const startAngle = 180; // degrees (left side)
  const endAngle = 0; // degrees (right side)
  const totalAngle = startAngle - endAngle;

  // Convert percentage (0-50) to angle
  const currentAngle = startAngle - (percentValue / 50) * totalAngle;
  const currentAngleRad = (currentAngle * Math.PI) / 180;
  const handleX = centerX + radius * Math.cos(currentAngleRad);
  const handleY = centerY - radius * Math.sin(currentAngleRad);

  // Handle mouse/touch interactions
  const handleInteraction = (clientX, clientY) => {
    if (readOnly || !svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Calculate angle from center to mouse position
    const dx = x - centerX;
    const dy = centerY - y; // Inverted because SVG y increases downward
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Normalize angle to 0-180 range
    if (angle < 0) angle = 0;
    if (angle > 180) angle = 180;

    // Convert angle to percentage (0-50)
    const newPercent = Math.round(((180 - angle) / 180) * 50);
    const clampedPercent = Math.max(0, Math.min(50, newPercent));

    setLocalValue(clampedPercent);
    if (onChange) {
      onChange(clampedPercent / 100);
    }
  };

  const handleMouseDown = (e) => {
    if (readOnly) return;
    setIsDragging(true);
    handleInteraction(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    if (condensedProp) {
      handleBarInteraction(e.clientX);
    } else {
      handleInteraction(e.clientX, e.clientY);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (readOnly) return;
    setIsDragging(true);
    const touch = e.touches[0];
    handleInteraction(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    if (condensedProp) {
      handleBarInteraction(touch.clientX);
    } else {
      handleInteraction(touch.clientX, touch.clientY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add/remove global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, percentValue]);

  // Horizontal bar interaction handlers for condensed mode
  const handleBarInteraction = (clientX) => {
    if (readOnly || !barRef.current) return;

    const rect = barRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const width = rect.width;

    // Calculate percentage (0-50)
    const rawPercent = (x / width) * 50;
    const clampedPercent = Math.max(0, Math.min(50, Math.round(rawPercent)));

    setLocalValue(clampedPercent);
    if (onChange) {
      onChange(clampedPercent / 100);
    }
  };

  const handleBarMouseDown = (e) => {
    if (readOnly) return;
    setIsDragging(true);
    handleBarInteraction(e.clientX);
  };

  const handleBarTouchStart = (e) => {
    if (readOnly) return;
    setIsDragging(true);
    const touch = e.touches[0];
    handleBarInteraction(touch.clientX);
  };

  const handleBarKeyDown = (e) => {
    if (readOnly) return;
    let newValue = percentValue;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      newValue = Math.max(0, percentValue - 1);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      newValue = Math.min(50, percentValue + 1);
    }

    if (newValue !== percentValue) {
      setLocalValue(newValue);
      if (onChange) {
        onChange(newValue / 100);
      }
    }
  };

  // Create SVG arc path (semicircle from left to right, sweeping UP)
  const startX = centerX - radius;
  const startY = centerY;
  const endX = centerX + radius;
  const endY = centerY;

  // Full arc path for background and gradient
  const arcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;

  // Remaining arc path from current position to end (for gray overlay)
  const remainingArcPath = `M ${handleX} ${handleY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;

  return (
    <div className={classNames(container, { [condensed]: condensedProp })}>
      <div className={label_with_tooltip}>
        <div className={center_label}>Debt-to-Income Ratio</div>
        <Tooltip
          content="Your debt-to-income ratio (DTI) is the percentage of your monthly gross income that goes toward paying debts. Lower ratios typically qualify for better loan terms."
          position="top"
        >
          <div className={info_icon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <circle cx="12" cy="8" r="0.5" fill="currentColor" />
            </svg>
          </div>
        </Tooltip>
      </div>

      {condensedProp ? (
        // HORIZONTAL BAR VERSION for condensed mode
        <>
          <div className={instruction_text}>SLIDE TO ADJUST</div>
          <div className={bar_container}>
            <div
              className={bar_track}
              ref={barRef}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={50}
              aria-valuenow={percentValue}
              aria-label="Debt-to-Income Ratio"
              tabIndex={readOnly ? -1 : 0}
              onKeyDown={handleBarKeyDown}
              onMouseDown={handleBarMouseDown}
              onTouchStart={handleBarTouchStart}
              style={{ cursor: readOnly ? 'default' : 'pointer' }}
            >
              <div
                className={bar_fill}
                style={{
                  width: `${(percentValue / 50) * 100}%`,
                  backgroundSize: `${percentValue > 0 ? (50 / percentValue) * 100 : 5000}% 100%`,
                  backgroundPosition: '0 0'
                }}
              />
              <div
                className={bar_handle}
                style={{
                  left: `${(percentValue / 50) * 100}%`,
                  cursor: readOnly ? 'default' : 'grab'
                }}
              />
            </div>
            <div className={bar_values}>
              <span className={bar_percentage}>{percentValue}%</span>
              <span className={bar_status} style={{ color: status.color }}>
                ▲ {status.text}
              </span>
            </div>
          </div>
        </>
      ) : (
        // ARC VERSION for normal mode
        <>
          <div className={instruction_text}>SLIDE TO ADJUST</div>
          <div className={track}>
            <svg
              ref={svgRef}
              width="320"
              height="180"
              viewBox="0 0 320 180"
              style={{
                cursor: readOnly ? 'default' : 'pointer'
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <defs>
                <linearGradient
                  id="dti-gradient"
                  x1={startX}
                  y1={centerY}
                  x2={endX}
                  y2={centerY}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#22c55e" />
                  <stop offset="64%" stopColor="#eab308" />
                  <stop offset="100%" stopColor="#ef4444" />
                </linearGradient>
              </defs>

              <path
                d={arcPath}
                fill="none"
                stroke="#d1d5db"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />

              <path
                d={arcPath}
                fill="none"
                stroke="url(#dti-gradient)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />

              <path
                d={remainingArcPath}
                fill="none"
                stroke="#d1d5db"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />

              <circle
                cx={handleX}
                cy={handleY}
                r="6"
                fill="white"
                stroke="white"
                strokeWidth="2"
                style={{ cursor: readOnly ? 'default' : 'pointer' }}
              />
            </svg>

            <div className={legend_wrapper}>
              <div className={center_value}>{percentValue}%</div>
              <div className={status_indicator} style={{ color: status.color }}>
                ▲ {status.text}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

DebtToIncomeSlider.propTypes = {
  /**
   * Current debt-to-income ratio (decimal, e.g., 0.28 for 28%)
   */
  value: PropTypes.number.isRequired,

  /**
   * Callback when slider value changes (receives decimal value)
   */
  onChange: PropTypes.func,

  /**
   * If true, slider is display-only and cannot be dragged
   */
  readOnly: PropTypes.bool,

  /**
   * If true, slider scales down for condensed/narrow containers
   */
  condensed: PropTypes.bool
};

export default DebtToIncomeSlider;
